// Structures on the path of a move (§31 S1, design "Infrastructure is
// Vulnerable"). A structure stands *on* a cell. An **Obstruction** stops every
// non-jumping move and is attacked where it stands (`blowOntoStructure`);
// anything else is **plowed**: an enemy mover destroys it in passing, up to
// `RULES.plowBudget` HP along one path, and its blow loses what it spent. Own
// and allied ones are crossed and stood on harmlessly. A jump crosses nothing
// and crushes what it lands on.
//
// Every board with no structures pays one size test per emitter, so perft is
// unmoved by construction.

import { fileOf, rankOf, type Side, square } from "../board";
import { type Move, NO_BREAK } from "../move";
import { areEnemies, obstructsAt, type Position, structureHealthAt } from "../position";
import { RULES } from "../rules";

/** `enterCost` for a square no non-jumping move may enter. */
export const WALL = -1;

/**
 * What a non-jumping mover of `us` pays to cross `sq`, which holds a structure
 * and no piece: `WALL` for an obstruction (anyone's — your own wall stops you
 * too), 0 for an own or allied one, else its HP.
 */
export function enterCost(pos: Position, sq: number, us: Side): number {
  if (obstructsAt(pos, sq)) return WALL;
  const owner = pos.structures.get(sq);
  if (owner === undefined || !areEnemies(pos, us, owner)) return 0;
  return structureHealthAt(pos, sq);
}

/** May one more structure of `cost` HP be plowed on a path that spent `spent`? */
export const withinBudget = (spent: number, cost: number): boolean =>
  spent + cost <= RULES.plowBudget;

/**
 * Does a mover of `us` landing on `sq` crush what stands there — a
 * non-obstruction structure of an enemy's? A jump's landing and every path's
 * end ask this; an own one is stood on.
 */
export function crushes(pos: Position, sq: number, us: Side): boolean {
  if (pos.structures.size === 0 || obstructsAt(pos, sq)) return false;
  const owner = pos.structures.get(sq);
  return owner !== undefined && areEnemies(pos, us, owner);
}

/**
 * Does a piece **shoved** onto `sq` break what stands there? Any
 * non-obstruction structure, whoever owns it (design example 1: "this also
 * breaks any structure the dreadnought may have been pushed into"). A shove
 * into an obstruction is refused by `standable` before this is asked.
 */
export const breaksOnLanding = (pos: Position, sq: number): boolean =>
  pos.structures.size > 0 && pos.structures.has(sq) && !obstructsAt(pos, sq);

/** A new list one square longer; the old one stays valid for earlier moves. */
export const extend = (list: readonly number[], sq: number): readonly number[] =>
  list.length === 0 ? [sq] : [...list, sq];

/** Stamp a path's breakage and spend onto a move. Returns it. */
export function plowed(move: Move, broken: readonly number[], spent: number): Move {
  if (broken.length > 0) move.broken = broken;
  move.spent = spent;
  return move;
}

/**
 * Is the line from `a` to `b` free of **obstructions** (§31 S1 — "block
 * line-of-sight for ranged attacks")? Every square strictly between, on the
 * straight line between the two centres, rounded to the nearest square
 * (`Math.round`, so deterministic). The endpoints are not asked: a gun does
 * not block itself, and a target standing on a wall is not behind it.
 */
export function clearLine(pos: Position, a: number, b: number): boolean {
  if (pos.obstructions.size === 0) return true;
  const fa = fileOf(a);
  const ra = rankOf(a);
  const df = fileOf(b) - fa;
  const dr = rankOf(b) - ra;
  const n = Math.max(Math.abs(df), Math.abs(dr));
  for (let i = 1; i < n; i++) {
    const sq = square(fa + Math.round((df * i) / n), ra + Math.round((dr * i) / n));
    if (obstructsAt(pos, sq)) return false;
  }
  return true;
}

export { NO_BREAK };
